import {
  ActionBar,
  ChakraBox,
  ChakraDivider,
  ChakraDrawer,
  ChakraDrawerBody,
  ChakraDrawerCloseButton,
  ChakraDrawerContent,
  ChakraDrawerHeader,
  ChakraDrawerOverlay,
  FAIconName,
  SkeletonLoader,
  Text,
  ToastMessage,
} from '@equip.health/ui';
import { useCallback, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { capitalizeFirstLetter } from '~/lib/common';
import {
  GO_TO_PATIENT_PROFILE,
  NEW_ORDER_LAB_TEXT,
} from '~/lib/constants/patients.constants';
import { GENERIC_API_FAILURE_MESSAGE } from '~/lib/constants/profile.constants';

import { Path } from '~/routes/constants';

import { useGetLabNetworkUrlQuery } from '~/store/services/labs';

interface OrderLabDrawerProps {
  isOpen: boolean;
  closeDrawer: () => void;
  patientId: string;
  onLabOrderSubmitted: () => void;
  emptyFields?: string[];
}

export const OrderLabDrawer = ({
  isOpen,
  closeDrawer,
  patientId,
  onLabOrderSubmitted,
  emptyFields,
}: OrderLabDrawerProps) => {
  const TOP_LEVEL_ID = 'order-lab';
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const isLabOrderEligible = emptyFields?.length === 0;
  const { data: labNetworkUrl, isError } = useGetLabNetworkUrlQuery(patientId, {
    refetchOnMountOrArgChange: true,
    skip: !isLabOrderEligible,
  });

  const iframeRef = useRef<HTMLIFrameElement>(null);

  const history = useHistory();

  const SUCCESS = 'success';
  const RESPONSE_CODE = 'responseCode';

  const checkIframeUrl = useCallback(() => {
    const iframe = iframeRef.current;
    if (iframe) {
      try {
        const iframeWindow = iframe?.contentWindow;
        if (labNetworkUrl?.url && iframeWindow) {
          const currentUrl = iframeWindow.location.href;
          if (currentUrl !== labNetworkUrl?.url) {
            const url = new URL(currentUrl);
            const responseCode = url.searchParams.get(RESPONSE_CODE);
            closeDrawer();
            if (responseCode?.toLowerCase() === SUCCESS) {
              onLabOrderSubmitted();
            }
          }
        }
      } catch (error) {
        console.error(error);
      }
    }
  }, [labNetworkUrl?.url]);

  const handleViewProfile = () => {
    history.push(`${Path.MAUD_PATIENTS}/${patientId}/profile`);
    closeDrawer();
  };

  const handleIframeLoad = useCallback(() => {
    checkIframeUrl();
    setIsIframeLoaded(true);
  }, [checkIframeUrl]);

  const missingFieldsMessage = capitalizeFirstLetter(
    `${emptyFields.join(', ')} ${emptyFields.length > 1 ? 'are' : 'is'} missing. Please add ${emptyFields.length > 1 ? 'these' : 'this'} before ordering labs.`,
  );

  return (
    <ChakraBox>
      {isOpen && (
        <ChakraDrawer
          id={`${TOP_LEVEL_ID}__drawer`}
          isOpen={isOpen}
          onClose={closeDrawer}
          placement="right"
          size="5xl"
        >
          <ChakraDrawerOverlay />
          <ChakraDrawerContent
            background="neutral.background.primary"
            height="100%"
            maxWidth="5xl"
          >
            <ChakraDrawerHeader>
              <Text variant="h4">{NEW_ORDER_LAB_TEXT}</Text>
            </ChakraDrawerHeader>
            <ChakraDivider id={`${TOP_LEVEL_ID}__divider`} />
            <ChakraDrawerCloseButton
              alignItems="center"
              right="20px"
              top="15px"
            />
            <ChakraDrawerBody>
              {isLabOrderEligible ? (
                <SkeletonLoader
                  height="100%"
                  id={`${TOP_LEVEL_ID}__skeleton-loader`}
                  isLoaded={isError || isIframeLoaded}
                >
                  <iframe
                    allowFullScreen
                    onLoad={handleIframeLoad}
                    ref={iframeRef}
                    src={labNetworkUrl?.url}
                    style={{ border: 'none', height: '100%', width: '100%' }}
                    title="lab-network-order"
                  />
                  {isError && (
                    <ToastMessage
                      icon={FAIconName.exclamationTriangleRegular}
                      id={`${TOP_LEVEL_ID}__warning`}
                      isFullWidth
                      text={GENERIC_API_FAILURE_MESSAGE}
                      variant="critical"
                    />
                  )}
                </SkeletonLoader>
              ) : (
                <ChakraBox paddingTop="16px">
                  <ActionBar
                    barIcon={FAIconName.exclamationTriangleRegular}
                    buttonIcon={FAIconName.userRegular}
                    buttonText={GO_TO_PATIENT_PROFILE}
                    id={`${TOP_LEVEL_ID}__action-bar`}
                    onButtonClick={handleViewProfile}
                    text={missingFieldsMessage}
                    variant="critical"
                  />
                </ChakraBox>
              )}
            </ChakraDrawerBody>
          </ChakraDrawerContent>
        </ChakraDrawer>
      )}
    </ChakraBox>
  );
};
